import {
    COMPACT_SHAPE_BEARING_X10,
    COMPACT_SHAPE_CUM_DIST,
    COMPACT_SHAPE_LAT,
    COMPACT_SHAPE_LON,
    COMPACT_SHAPE_STRIDE,
    SHAPE_POINTS_BIN_RECORD_BYTES,
} from "./compactConstants";
import { CompactShapeView } from "./CompactShapeView";

/**
 * Runtime storage for one deduplicated route shape (`shape_id`).
 *
 * Replaces POJO `{ lat, lon, … }[]` caches to avoid V8 heap-number boxing.
 * Populated from wire {@link ShapeWire.points_bin} via {@link coercePointsBin} and
 * {@link CompactShape.fromBuffer}. One shared instance per `shape_id` in {@link RealtimeCache}.
 *
 * Slot semantics per point (stride {@link COMPACT_SHAPE_STRIDE}):
 * - lat, lon: degrees (promoted from wire f32)
 * - cumDist: meters along the polyline from the first point
 * - bearingX10: wire u16 (0–3600), not degrees
 */
export class CompactShape {

    /**
     * Interleaved point buffer: `[lat₀, lon₀, cumDist₀, bearingX10₀, lat₁, …]`.
     * Length is `length × {@link COMPACT_SHAPE_STRIDE}`.
     */
    readonly data: Float64Array;

    /** Number of shape points (`data.length / COMPACT_SHAPE_STRIDE`). */
    readonly length: number;

    private constructor( data: Float64Array, length: number ) {
        this.data = data;
        this.length = length;
    }

    /**
     * Decodes `points_bin` into a compact shape buffer.
     *
     * Wire layout per point ({@link SHAPE_POINTS_BIN_RECORD_BYTES} bytes, little-endian):
     *
     * | Offset | Type | Field        |
     * |--------|------|--------------|
     * | 0      | f32  | lat          |
     * | 4      | f32  | lon          |
     * | 8      | f32  | cumDist      |
     * | 12     | u16  | bearing (×10)|
     *
     * Requires `pointsBin.byteLength % {@link SHAPE_POINTS_BIN_RECORD_BYTES} === 0`.
     * Wire f32/u16 values are promoted to f64 slots in `data`; the input buffer is not kept.
     *
     * @param pointsBin Normalized bytes (use {@link coercePointsBin} after HTTP decode).
     * @returns A {@link CompactShape} owning a new `Float64Array`.
     * @throws {Error} When length is zero or not a multiple of {@link SHAPE_POINTS_BIN_RECORD_BYTES}.
     */
    static fromBuffer( pointsBin: Uint8Array ): CompactShape {
        const byteLength = pointsBin.byteLength;
        if ( byteLength === 0 || byteLength % SHAPE_POINTS_BIN_RECORD_BYTES !== 0 ) {
            throw new Error( `Invalid points_bin length ${ byteLength }: expected a positive multiple of ${ SHAPE_POINTS_BIN_RECORD_BYTES }` );
        }

        const pointCount = byteLength / SHAPE_POINTS_BIN_RECORD_BYTES;
        const data = new Float64Array( pointCount * COMPACT_SHAPE_STRIDE );
        const view = new DataView( pointsBin.buffer, pointsBin.byteOffset, pointsBin.byteLength );

        for ( let i = 0; i < pointCount; i += 1 ) {
            const wireOffset = i * SHAPE_POINTS_BIN_RECORD_BYTES;
            const compactOffset = i * COMPACT_SHAPE_STRIDE;
            data[compactOffset + COMPACT_SHAPE_LAT] = view.getFloat32( wireOffset, true );
            data[compactOffset + COMPACT_SHAPE_LON] = view.getFloat32( wireOffset + 4, true );
            data[compactOffset + COMPACT_SHAPE_CUM_DIST] = view.getFloat32( wireOffset + 8, true );
            data[compactOffset + COMPACT_SHAPE_BEARING_X10] = view.getUint16( wireOffset + 12, true );
        }

        return new CompactShape( data, pointCount );
    }

    /**
     * Returns a zero-copy {@link CompactShapeView} for schedule bounds `[startIdx, endIdxInclusive]`.
     *
     * `shape_start_idx` / `shape_end_idx` on the trip schedule are **global** indices into this
     * shape; the view exposes **local** indices `0 .. count-1` for timeline construction.
     *
     * @param startIdx First point index in the parent shape (inclusive).
     * @param endIdxInclusive Last point index in the parent shape (inclusive).
     * @returns A view sharing `data` with the parent shape (no polyline copy).
     */
    slice( startIdx: number, endIdxInclusive: number ): CompactShapeView {
        const count = endIdxInclusive - startIdx + 1;

        return new CompactShapeView( this.data, startIdx, count );
    }

    /**
     * Latitude in degrees at a global shape index (non-hot-path helper).
     *
     * @param index Global point index in `[0, length)`.
     * @returns WGS-84 latitude.
     */
    latAt( index: number ): number {
        return this.data[index * COMPACT_SHAPE_STRIDE + COMPACT_SHAPE_LAT];
    }

    /**
     * Longitude in degrees at a global shape index (non-hot-path helper).
     *
     * @param index Global point index in `[0, length)`.
     * @returns WGS-84 longitude.
     */
    lonAt( index: number ): number {
        return this.data[index * COMPACT_SHAPE_STRIDE + COMPACT_SHAPE_LON];
    }

    /**
     * Cumulative distance along the shape from the start, in meters.
     *
     * @param index Global point index in `[0, length)`.
     * @returns Cumulative distance in meters.
     */
    cumDistAt( index: number ): number {
        return this.data[index * COMPACT_SHAPE_STRIDE + COMPACT_SHAPE_CUM_DIST];
    }

    /**
     * Bearing×10 wire value at a global shape index.
     *
     * @param index Global point index in `[0, length)`.
     * @returns Bearing multiplied by 10 (0–3600).
     */
    bearingX10At( index: number ): number {
        return this.data[index * COMPACT_SHAPE_STRIDE + COMPACT_SHAPE_BEARING_X10];
    }

}
